/**
 * AccountsAggregationPage component module.
 * @module advisor/pages/AccountsAggregationPage
 *
 */
import React, {useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import useSWR from 'swr';
import qs from 'query-string';
import {useLocation} from 'react-router-dom';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import history from '../../history';
import axiosInstance from '../../shared/utils/axios';
import {Column, DescriptiveText, Icon, LoadingSpinner, Page, Row, Tile} from '../../shared';
import YodleeFastlink from '../../shared/widgets/YodleeFastlink';
import {_openModal} from '../../shared/reducers/modals';
import {formatter} from '../../shared/utils/utils';

/**
 * AccountsAggregationPage component
 * @name AccountsAggregationPage
 */
const AccountsAggregationPage = ({
  session: {profile},
  openModal,
}) => {
  const unblockNavigationRef = useRef();
  const accountsListLocation = '/accounts';
  const {state: locationState, search: locationSearch} = useLocation();
  const previousLocation = locationState?.backLocation || accountsListLocation;
  const {flow, providerAccountId} = qs.parse(locationSearch);
  const yodleeFastlinkParams = flow && providerAccountId && {flow, providerAccountId};

  const [closing, setClosing] = useState(false);
  const {data: userJwt, error: error, isValidating: loading} = useSWR('yodlee/jwt');

  const navigateTo = (location) => {
    unblockNavigationRef.current();
    history.push(location);
  };
  const setupNavigationBlock = () => history.block(event => {
    const errorMessage = <DescriptiveText preset={'standard'} margin={'20px 0'} width={'100%'}>
      You are about to leave this page and disconnect from the institution. <br />
      Any changes you’ve made will not be saved. Do you wish to continue?
    </DescriptiveText>;

    /* Quick fix to allow user to logout without prompt */
    if (event.pathname === '/login') {
      return true;
    }

    openModal({
      type: 'ConfirmModal',
      title: 'DISCONNECTING',
      body: errorMessage,
      button: {
        text: 'Disconnect',
        width: '120px',
        action: () => navigateTo(event.pathname),
      },
    });

    return false;
  });

  const handleSuccessEvent = (event) => {
    if (event.flow === 'refresh') {
      const payload = {status: 'PENDING_REFRESH', errorMessage: null};
      return axiosInstance.put(`yodlee/sites/${event.providerAccountId}`, payload);
    }

    return axiosInstance.post('yodlee/sites', event);
  };
  const handleCloseEvent = (event) => {
    const aggregatedSites = (event.sites || []).filter(v => v.status !== 'ACTION_ABANDONED');
    const cancelledSites = (event.sites || []).filter(v => v.status === 'ACTION_ABANDONED');
    const isAnySiteAggregated = !!aggregatedSites.length;
    const isAggregationCancelled = !!cancelledSites.length;
    const isUserCloseClick = event.status === 'USER_CLOSE_ACTION';
    const isErrorCloseClick = !!event.code;

    setClosing(true);

    if (isAggregationCancelled) {
      return Promise
        .map(cancelledSites, s => axiosInstance.delete(`/yodlee/institutions/${s.providerAccountId}?local=true`))
        .then(() => isAnySiteAggregated ? navigateTo(accountsListLocation) : navigateTo(previousLocation))
        .catch(() => navigateTo(previousLocation));
    }
    if (isUserCloseClick || isErrorCloseClick) {
      return isAnySiteAggregated ? navigateTo(accountsListLocation) : navigateTo(previousLocation);
    }
    return navigateTo(accountsListLocation);
  };
  const onFastlinkSuccess = (event) => handleSuccessEvent(event);
  const onFastlinkClose = (event) => handleCloseEvent(event);
  const onFastlinkError = () => navigateTo(previousLocation);

  useEffect(() => {
    unblockNavigationRef.current = setupNavigationBlock();

    return () => unblockNavigationRef.current();
  }, []);

  return (
    <Page center={true}>
      <Tile key={'AccountsAggregation'}
            title={'CONNECT TO INSTITUTION'}
            icon={<Icon svg={'arrowCircle'} rotate={'180deg'} action={() => history.push(previousLocation)}/>}>
        <Column maxHeight={'calc(100vh - 150px)'}
                minHeight={'400px'}
                margin={'5px'}>
          {closing && <LoadingSpinner/>}
          {loading && <LoadingSpinner/>}
          {!loading && error && (
            <Row wrap={false}
                 borderRadius={'6px'}
                 margin={'5px 0'}
                 justifySubRow={'left'}
                 backgroundColor={'rgba(255,255,255,0.1)'}>
              <Icon svg={'attention'}
                    color={'#FFC003'}
                    height={'20px'}
                    width={'20px'}
                    margin={'5px 15px 5px 15px'}
              />
              <DescriptiveText margin={'5px'} alignment={'left'} layout={{grow: 1}}>
                Sorry, we experienced issues connecting to the institution. Please try again later, or you can reach out
                to {profile.brand.investorCSEmail} or call {formatter('phone')(profile.brand.investorCSPhone)}.
              </DescriptiveText>
            </Row>
          )}
          {!loading && !error && (
            <YodleeFastlink jwt={userJwt}
                            params={yodleeFastlinkParams}
                            onSuccess={onFastlinkSuccess}
                            onClose={onFastlinkClose}
                            onError={onFastlinkError}/>
          )}
        </Column>
      </Tile>
    </Page>
  );
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  openModal: modal => dispatch(_openModal(modal)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AccountsAggregationPage);

AccountsAggregationPage.propTypes = {
  /**
   * User Session, includes profile
   */
  session: PropTypes.shape({
    profile: PropTypes.shape({
      brand: PropTypes.shape({
        investorCSEmail: PropTypes.string.isRequired,
        investorCSPhone: PropTypes.string.isRequired,
      }),
    }),
  }).isRequired,
  /**
   * The open modal function to dispatch _openModal from Redux
   */
  openModal: PropTypes.func.isRequired,
};
